import React, { useEffect, useState } from "react";
import CustomListing from "../CustomListing";
import CustomRoomSelect from "../CustomRoomSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  setCheckIn,
  setCheckOut,
  setHotelName,
  setRooms,
  setSearchName,
} from "../../redux/website/search/SearchSlice";
import { useLocation, useNavigate } from "react-router";
import UniversalDateRange from "./UniversalDateRange";

const STORAGE_KEY = "@dhoorbin:hotelSearch";
const readSearch = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeSearch = (data) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota / private mode */
  }
};

const UniversalSearch = ({ IsShowHotel = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const Rooms = useSelector((state) => state.search.room);

  const dispatch = useDispatch();
  const TotalRoom = Rooms.reduce((total) => total + 1, 0);
  const TotalGuest = Rooms.reduce(
    (total, room) => total + room.adult + room.children,
    0,
  );
  const totalAdult = Rooms.reduce((total, room) => total + room.adult, 0);
  const totalChildren = Rooms.reduce((total, room) => total + room.children, 0);

  const [Room, setRoom] = useState([{ room: 1, adult: 2, children: 0 }]);
  // city row or hotel row or "" (default)
  const [Locations, setLocations] = useState("");

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 5);
  const weekLater = new Date();
  weekLater.setDate(today.getDate() + 6);

  const [range, setRange] = useState({
    start: tomorrow.toLocaleDateString("en-CA"),
    end: weekLater.toLocaleDateString("en-CA"),
  });
  const [IshowCalendar, setIshowCalendar] = useState(false);
  const [IsShowSearch, setIsShowSearch] = useState(false);
  const [IsShowRoom, setIsShowRoom] = useState(false);

  const isHotel = Locations && Locations.hotelName;

  // Keep redux rooms in sync with the local Room state so the display
  // (TotalRoom / adults / children) always reflects the current selection.
  useEffect(() => {
    dispatch(setRooms(Room));
  }, [Room, dispatch]);

  // Hydrate once: sessionStorage is primary, URL params are the fallback.
  useEffect(() => {
    const saved = readSearch();
    const source = saved || {
      city: params.get("city"),
      cityId: params.get("cityId"),
      division: params.get("division"),
      divisionId: params.get("divisionId"),
      country: params.get("country"),
      hotelName: params.get("hotelName"),
      hotelId: params.get("hotelId"),
      checkIn: params.get("checkIn"),
      checkOut: params.get("checkOut"),
    };

    // If the SEARCH page was opened with a blank URL (no params) but we have
    // a saved search, rebuild the URL from storage so the link stays shareable.
    // Only do this on /product/search — never on the hotel details page, where
    // UniversalSearch is just an inline date/room editor (IsShowHotel=false).
    const onSearchPage =
      IsShowHotel && location.pathname.includes("/product/search");
    const hasUrlParams = location.search && location.search.length > 1;
    if (onSearchPage && !hasUrlParams && saved) {
      const sp = new URLSearchParams();
      Object.entries(saved).forEach(([k, v]) => {
        if (k === "roomList" || v === undefined || v === null || v === "")
          return;
        sp.set(k, v);
      });
      const qs = sp.toString();
      if (qs) navigate(`/product/search?${qs}`, { replace: true });
    }

    if (source.checkIn && source.checkOut) {
      setRange({ start: source.checkIn, end: source.checkOut });
      dispatch(setCheckIn(source.checkIn));
      dispatch(setCheckOut(source.checkOut));
    }

    if (Array.isArray(saved?.roomList) && saved.roomList.length > 0) {
      setRoom(saved.roomList);
      dispatch(setRooms(saved.roomList)); // keep redux (and the display) in sync
    }

    if (source.hotelName) {
      setLocations({ hotelId: source.hotelId, hotelName: source.hotelName });
      dispatch(setHotelName(source.hotelName));
      dispatch(setSearchName(""));
    } else if (source.city) {
      setLocations({
        cityId: source.cityId,
        cityName: source.city,
        divisionId: source.divisionId,
        divisionName: source.division,
        countryName: source.country,
      });
      dispatch(setSearchName(source.city));
      dispatch(setHotelName(""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowCalendar = () => {
    setIshowCalendar(!IshowCalendar);
    setIsShowSearch(false);
    setIsShowRoom(false);
  };
  const handleShowSearch = () => {
    setIsShowSearch(!IsShowSearch);
    setIshowCalendar(false);
    setIsShowRoom(false);
  };
  const handleShowRoom = () => {
    setIsShowRoom(!IsShowRoom);
    setIshowCalendar(false);
    setIsShowSearch(false);
  };

  // Keep redux in sync when a new selection is made from the picker.
  useEffect(() => {
    if (Locations !== "") {
      setIsShowSearch(false);
    }
    if (isHotel) {
      dispatch(setHotelName(Locations.hotelName || ""));
      dispatch(setSearchName(""));
    } else if (Locations && Locations.cityName) {
      dispatch(setSearchName(Locations.cityName || ""));
      dispatch(setHotelName(""));
    }
  }, [Locations, dispatch, isHotel]);

  const handleSearch = () => {
    if (!range.end) {
      setIshowCalendar(true);
      return;
    }

    // Build the flat data (same shape as URL params) and reuse it for
    // sessionStorage, redux and the URL.
    const data = {
      checkIn: range.start,
      checkOut: range.end,
      room: TotalRoom,
      guest: TotalGuest,
      adult: totalAdult,
      children: totalChildren,
      roomList: Room,
    };

    if (isHotel) {
      data.hotelName = Locations.hotelName;
      if (Locations.hotelId) data.hotelId = Locations.hotelId;
    } else if (Locations && Locations.cityName) {
      data.city = Locations.cityName;
      if (Locations.cityId) data.cityId = Locations.cityId;
      if (Locations.divisionName) data.division = Locations.divisionName;
      if (Locations.divisionId) data.divisionId = Locations.divisionId;
      if (Locations.countryName) data.country = Locations.countryName;
    } else {
      // keep whatever was already stored (e.g. on the details page where the
      // location picker is hidden), only update dates/rooms
      const prev = readSearch() || {};
      if (prev.hotelName) {
        data.hotelName = prev.hotelName;
        data.hotelId = prev.hotelId;
      } else {
        data.city = prev.city || "Cox's Bazar";
        data.cityId = prev.cityId;
        data.division = prev.division;
        data.divisionId = prev.divisionId;
        data.country = prev.country;
      }
    }

    // persist + redux
    writeSearch(data);
    dispatch(setRooms(Room));
    dispatch(setCheckIn(range.start));
    dispatch(setCheckOut(range.end));

    // only navigate when the hotel search (full bar) is shown
    if (IsShowHotel) {
      const sp = new URLSearchParams();
      Object.entries(data).forEach(([k, v]) => {
        if (k === "roomList" || v === undefined || v === null || v === "")
          return;
        sp.set(k, v);
      });
      navigate(`/product/search?${sp.toString()}`);
    }

    setIshowCalendar(false);
    setIsShowSearch(false);
    setIsShowRoom(false);
  };

  // display name for the selection
  const displayName = isHotel
    ? Locations.hotelName
    : Locations && Locations.cityName
      ? Locations.cityName
      : "Cox's Bazar";

  return (
    <>
      <div className="w-full lg:hidden relative">
        <div className="flex gap-4 w-full ">
          <button
            onClick={handleShowCalendar}
            className="space-y-1 border text-start w-full lg:w-[220px] p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
          >
            <p className="font-extralight text-xs ">Check In</p>
            <p
              onClick={handleShowCalendar}
              className="text-md lg:text-md uppercase font-extrabold text-[#2155ad]"
            >
              {new Date(range.start).toLocaleString("en-US", {
                month: "short",
              })}
              '{new Date(range.start).getDate()}
            </p>
            <p className="text-black/50 text-xs">
              {" "}
              {new Date(range.start).toLocaleString("en-US", {
                weekday: "long",
              })}
            </p>
          </button>
          <button
            onClick={handleShowCalendar}
            className="space-y-1 border w-full text-start p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
          >
            <p className="font-extralight text-xs ">Check Out</p>
            {range.end ? (
              <>
                <p
                  onClick={handleShowCalendar}
                  className="text-md lg:text-md uppercase font-extrabold text-[#2155ad]"
                >
                  {new Date(range.end).toLocaleString("en-US", {
                    month: "short",
                  })}
                  '{new Date(range.end).getDate()}
                </p>
                <p className="text-black/50 text-xs">
                  {" "}
                  {new Date(range.end).toLocaleString("en-US", {
                    weekday: "long",
                  })}
                </p>
              </>
            ) : (
              <p className="text-xs text-black/50 mt-2">Select end date</p>
            )}
          </button>
          {IshowCalendar && (
            <div className="absolute z-30 top-28 left-1/2 transform -translate-x-1/2">
              <UniversalDateRange
                setIshowCalendar={setIshowCalendar}
                setRange={setRange}
                range={range}
              />
            </div>
          )}
        </div>
        <div className="mt-2">
          <button
            onClick={handleSearch}
            className="w-full h-full py-3 font-semibold text-md rounded-lg text-white bg-[#0a1d46]"
          >
            Update
          </button>
        </div>
      </div>
      <div className="w-full h-auto hidden lg:block">
        <div className=" items-center justify-start gap-4 hidden lg:flex">
          {IsShowHotel && (
            <div className="relative w-full">
              <button
                onClick={handleShowSearch}
                className="space-y-1 border w-full p-2 text-start rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
              >
                <p className="font-extralight text-xs">City | Hotel | Resort</p>
                <p className="text-md lg:text-md uppercase font-extrabold text-[#2155ad]">
                  {displayName}
                </p>
                <p className="text-black/50 text-xs">
                  {!isHotel && Locations.countryName}
                  {Locations === "" && "Bangladesh"}
                </p>
              </button>
              {IsShowSearch && (
                <div className="absolute z-30 top-24 left-1/2 border rounded-lg transform -translate-x-1/2 shadow-lg w-full">
                  <CustomListing
                    Locations={Locations}
                    setLocations={setLocations}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex gap-4 w-full relative">
            <button
              onClick={handleShowCalendar}
              className="space-y-1 border text-start w-full lg:w-[220px] p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
            >
              <p className="font-extralight text-xs ">Check In</p>
              <p
                onClick={handleShowCalendar}
                className="text-md lg:text-md uppercase font-extrabold text-[#2155ad]"
              >
                {new Date(range.start).toLocaleString("en-US", {
                  month: "short",
                })}
                '{new Date(range.start).getDate()}
              </p>
              <p className="text-black/50 text-xs">
                {" "}
                {new Date(range.start).toLocaleString("en-US", {
                  weekday: "long",
                })}
              </p>
            </button>
            <button
              onClick={handleShowCalendar}
              className="space-y-1 border w-full lg:w-[220px] text-start p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
            >
              <p className="font-extralight text-xs ">Check Out</p>
              {range.end ? (
                <>
                  <p
                    onClick={handleShowCalendar}
                    className="text-md lg:text-md uppercase font-extrabold text-[#2155ad]"
                  >
                    {new Date(range.end).toLocaleString("en-US", {
                      month: "short",
                    })}
                    '{new Date(range.end).getDate()}
                  </p>
                  <p className="text-black/50 text-xs">
                    {" "}
                    {new Date(range.end).toLocaleString("en-US", {
                      weekday: "long",
                    })}
                  </p>
                </>
              ) : (
                <p className="text-xs text-black/50 mt-2">Select end date</p>
              )}
            </button>
            {IshowCalendar && (
              <div className="absolute z-30 top-24 left-1/2 transform -translate-x-1/2">
                <UniversalDateRange
                  setIshowCalendar={setIshowCalendar}
                  setRange={setRange}
                  range={range}
                />
              </div>
            )}
          </div>

          <div className="relative w-full">
            <button
              onClick={handleShowRoom}
              className="space-y-1 border text-start w-full p-2 rounded-lg cursor-pointer bg-white hover:bg-[#2154ad2a] duration-300"
            >
              <p className="font-extralight text-xs ">Rooms and Guests</p>
              <p className="text-md lg:text-md uppercase font-extrabold text-[#2155ad]">
                {TotalRoom} Room | {TotalGuest} Guests
              </p>
              <p className="text-black/50 text-xs">
                {totalAdult} Adults, {totalChildren} children
              </p>
            </button>
            {IsShowRoom && (
              <div className="absolute z-30 top-24 left-2/4 transform -translate-x-1/2">
                <CustomRoomSelect Room={Room} setRoom={setRoom} />
              </div>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="w-full h-full py-7 font-bold rounded-lg text-white bg-[#0a1d46]"
          >
            Update
          </button>
        </div>
      </div>
    </>
  );
};

export default UniversalSearch;
